import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { arrayMove } from "@dnd-kit/sortable";
import { useDebouncedCallback } from "use-debounce";
import { Trans as T } from 'react-i18next';

import useStatuses from "../../hooks/useStatuses";
import useMutateStatus from "../../hooks/useMutateStatus";

import EditableField from "../../common/EditableField/EditableField";
import Sortable from "../../common/Sortable/Sortable";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";
import AddLeadStatus from "../../common/Leads/AddLeadStatus";
import StatusColorPicker from "./StatusColorPicker";
import TrashIcon from "../../assets/icons/TrashIcon";

import './LeadStatusManager.scss';

export default function LeadStatusManager({ categoryId, showSavedToast }) {
  const [statusOrder, setStatusOrder] = useImmer(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdStatusId, setCreatedStatusId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const { data: statuses, isLoading } = useStatuses({ categoryId });
  const { updateStatus, bulkUpdateStatus, deleteStatus } = useMutateStatus();

  const newItemRef = useRef();

  useEffect(() => {
    const order = statuses?.sort((a, b) => a.order_index - b.order_index).map(status => ({ id: status.id }));
    order && setStatusOrder(order);
  }, [statuses, setStatusOrder]);

  useLayoutEffect(() => {
    if (createdStatusId) {
      newItemRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [createdStatusId]);

  const debouncedRemoveCreatedStatus = useDebouncedCallback(() => {
    setCreatedStatusId(null);
  }, 2000);

  if (isLoading || !statusOrder) {
    return null;
  }

  const onCreateStatus = newId => {
    debouncedRemoveCreatedStatus();
    setCreatedStatusId(newId);
  }

  const onDelete = id => {
    setDeleteId(id);
    setShowDelete(true);
  }

  const confirmDelete = () => {
    deleteStatus.mutate(deleteId, { onSuccess: () => showSavedToast('Lead status deleted') });
    setShowDelete(false);
  }

  const onChangeColor = (id, color) => {
    updateStatus.mutate({ data: { color }, id }, { onSuccess: () => showSavedToast() });
  }

  const onChangeOrder = (activeIndex, overIndex) => {
    const lowestIndex = Math.min(activeIndex, overIndex);
    const highestIndex = Math.max(activeIndex, overIndex);

    const newOrder = arrayMove(statusOrder, activeIndex, overIndex);

    const data = [];

    for (let i = lowestIndex; i <= highestIndex; ++i) {
      const item = statuses.find(status => status.id === newOrder[i].id);
      data.push({ id: item.id, order_index: i });
    }

    bulkUpdateStatus.mutate({ data, categoryId });
    setStatusOrder(newOrder);
  }

  const toggleAdd = () => {
    setShowCreate(true);
  }

  const onEditSucces = () => {
    showSavedToast('Lead status updated')
  }

  const renderItem = item => {
    const status = statuses.find(status => item.id === status.id);

    // when deleting a status, the data might be insconsistent, delete if no status found
    if (!status) return;

    const newlyCreated = createdStatusId === status.id;

    return (
      <Sortable.Item id={status.id} newlyCreated={newlyCreated} columnView>
        <div className="row-handle" ref={newItemRef}><Sortable.DragHandle /></div>
        <div className="row-name">
          <EditableField field='name' initialValue={status.name} id={status.id} onEditSucces={onEditSucces} mutation={useMutateStatus} updateName='updateStatus' />
        </div>
        <div className="row-color">
          <StatusColorPicker color={status.color} onChange={color => onChangeColor(status.id, color)} />
        </div>
        <div className="row-description">
          <EditableField field='description' addLongName initialValue={status.description} id={status.id} onEditSucces={onEditSucces} mutation={useMutateStatus} updateName='updateStatus' />
        </div>
        <div className="row-delete">
          { status.can_be_deleted && <button className='delete-btn' onClick={() => onDelete(status.id)}><TrashIcon /> <T>Remove</T></button> }
        </div>
      </Sortable.Item>
    );
  }

  return (
    <div className="__lead-status-manager">
      <div className="title-actions">
        <h2><T>Lead Status</T></h2>
        { !showCreate && <button className="main-action" onClick={toggleAdd}><T>＋ Add Status</T></button> }
      </div>

      { showCreate && <AddLeadStatus onCreateSuccess={onCreateStatus} onHide={() => setShowCreate(false)} statuses={statuses} /> }

      <div className="statuses-table">
        <div className="table-head">
          <div className="row-handle" />
          <div className="row-name"><T>Name</T></div>
          <div className="row-color"><T>Color</T></div>
          <div className="row-description"><T>Description</T></div>
          <div className="row-delete" />
        </div>
        <Sortable items={statusOrder} onChange={onChangeOrder} renderItem={renderItem} columnView />
      </div>

      <ConfirmationModal show={showDelete} title='Delete Lead Status' confirmText='Delete' onHide={() => setShowDelete(false)} onConfirm={confirmDelete}>
        <p>Are you sure you want to delete this status?</p>
        <p>Any lead assigned to this status will have its status reset to the default category.</p>
      </ConfirmationModal>
    </div>
  );
}
